'use client';

import { createContext, useContext, useEffect, useState, useMemo, ReactNode } from 'react';
import { ensureCloudSdkBrowserForSearch } from 'lib/search/ensure-cloud-sdk-browser';
import scConfig from 'sitecore.config';

interface SearchInitializationContextType {
  isSearchInitialized: boolean;
  searchInitializationError?: Error | null;
}

const SearchInitializationContext = createContext<SearchInitializationContextType | undefined>(
  undefined
);

interface SearchInitializationProviderProps {
  children: ReactNode;
  siteName?: string;
  contextId?: string;
}

/**
 * Provider for managing Sitecore Cloud SDK Search initialization.
 * Ensures that search is initialized before any search components try to fetch data.
 */
export function SearchInitializationProvider({
  children,
  siteName,
  contextId,
}: SearchInitializationProviderProps) {
  const [isSearchInitialized, setIsSearchInitialized] = useState(false);
  const [searchInitializationError, setSearchInitializationError] = useState<Error | null>(null);

  useEffect(() => {
    // Initialize search on component mount
    ensureCloudSdkBrowserForSearch({
      sitecoreEdgeContextId: contextId || scConfig.api?.edge?.clientContextId,
      siteName: siteName || scConfig.defaultSite,
      sitecoreEdgeUrl:
        process.env.NEXT_PUBLIC_SITECORE_EDGE_URL || process.env.SITECORE_EDGE_URL || undefined,
    })
      .then(() => {
        setIsSearchInitialized(true);
      })
      .catch((err) => {
        console.error('Failed to initialize search SDK:', err);
        setSearchInitializationError(err as Error);
        // Still mark as initialized so UI doesn't hang forever
        setIsSearchInitialized(true);
      });
  }, [contextId, siteName]);

  const contextValue = useMemo(
    () => ({ isSearchInitialized, searchInitializationError }),
    [isSearchInitialized, searchInitializationError]
  );

  return (
    <SearchInitializationContext.Provider value={contextValue}>
      {children}
    </SearchInitializationContext.Provider>
  );
}

/**
 * Hook to access search initialization state.
 * Use this in search components to ensure SDK is ready before fetching.
 */
export function useSearchInitialization(): SearchInitializationContextType {
  const context = useContext(SearchInitializationContext);
  if (!context) {
    throw new Error('useSearchInitialization must be used within SearchInitializationProvider');
  }
  return context;
}
